import React, { useRef } from 'react'
import { trackAudioPlay } from 'lib/analytics/events'
import CommonnessCard from '../commonness-card'
import SaveWordButton from '../save-word-button'

type AudioButtonProps = {
   label: string
   // The accent this button plays, as reported to analytics ('gb' / 'us').
   voice: string
   audioFiles: string[]
}

const AudioButton = ({ label, voice, audioFiles }: AudioButtonProps) => {
   const audioRef = useRef<HTMLAudioElement | null>(null)

   if (!audioFiles.length) return null

   const play = () => {
      const el = audioRef.current
      if (!el) return
      trackAudioPlay('en', voice)
      el.load()
      el.play()
   }

   return (
      <button
         type="button"
         className="inline-flex items-center justify-center h-[40px] shrink-0 border border-hairline bg-transparent text-bodyBlack cursor-pointer transition-all duration-150 hover:bg-usageBg hover:border-textMuted gap-[7px] pr-[14px] pl-[12px] rounded-full [&_.material-icons]:text-[20px]"
         title={`Listen — ${label} English`}
         aria-label={`Listen — ${label} English`}
         onClick={play}
      >
         <span className="material-icons">volume_up</span>
         <span className="text-[12px] font-bold tracking-[0.4px]">{label}</span>
         <audio ref={audioRef} preload="none" src={audioFiles[0]} />
      </button>
   )
}

// The Thai respelling is authored hyphen-separated (คอม-พิว-เต้อ). The syllable
// hyphens and the enclosing brackets get a touch of air so the Thai reads as
// syllables rather than as one punctuated string.
const ThaiPhonetic = ({ phonetic }: { phonetic: string }) => {
   const syllables = phonetic.split('-')

   return (
      // No `.thai` class here: it hard-sets colour and size in tailwind.css and
      // would win over these utilities, pulling the respelling back to near-black.
      <div className="font-thai text-[20px] sm:text-[24px] font-normal leading-[1.4] text-textMuted whitespace-nowrap">
         <span className="mr-[0.22em]">[</span>
         {syllables.map((syllable, i) => (
            <React.Fragment key={i}>
               {i > 0 && <span className="px-[0.04em]">-</span>}
               {syllable}
            </React.Fragment>
         ))}
         <span className="ml-[0.22em]">]</span>
      </div>
   )
}

type Props = {
   word: string
   // 0–1; drives the friendly tier label + meter in the hero's commonness card.
   commonnessScore: number
   gbAudioFiles: string[]
   usAudioFiles: string[]
   // Hyphen-syllable Thai respelling (leads the row — it is what a Thai learner
   // reads) and bare UK IPA (rendered /…/ as a small companion). The row is
   // simply absent until the pronunciation data is authored for this word.
   ipaUk?: string | null
   thaiPhonetic?: string | null
}

// The English page's hero, laid out like the Thai page's (see thai/main-word-bar):
// headword and its listen/save buttons on the left, a pronunciation row (UK IPA
// + Thai phonetic respelling) beneath, the commonness card in the right rail.
// No nutshell: commonness blurbs are authored for Thai headwords only.
const MainWordBar = ({
   word,
   commonnessScore,
   gbAudioFiles,
   usAudioFiles,
   ipaUk,
   thaiPhonetic,
}: Props) => {
   return (
      <div>
         <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-x-[44px] gap-y-[28px] sm:gap-y-0 items-center">
            <div className="min-w-0">
               <div className="flex items-center gap-[16px] flex-wrap">
                  <h1 className="text-[34px] min-[400px]:text-[40px] sm:text-[54px] font-bold tracking-[-0.01em] text-bodyBlack leading-[1.1] m-0">
                     {word}
                  </h1>
                  <div className="flex items-center gap-[10px]">
                     <AudioButton label="UK" voice="gb" audioFiles={gbAudioFiles} />
                     <AudioButton label="US" voice="us" audioFiles={usAudioFiles} />
                     <SaveWordButton lang="eng" word={word} />
                  </div>
               </div>

               {(ipaUk || thaiPhonetic) && (
                  <div className="flex items-baseline gap-[14px] flex-wrap mt-[12px]">
                     {thaiPhonetic && <ThaiPhonetic phonetic={thaiPhonetic} />}
                     {ipaUk && (
                        <span className="font-translit text-[13px] italic text-textMuted whitespace-nowrap">
                           /{ipaUk}/
                        </span>
                     )}
                  </div>
               )}
            </div>

            <div className="flex flex-col gap-[14px]">
               <CommonnessCard commonnessScore={commonnessScore} />
            </div>
         </div>
      </div>
   )
}

export default MainWordBar
